import { useMemo } from "react";

import {
    AbfLoanExpanded,
    summarizeLedgerAccount,
    calculateHealthRatio,
    calculateLoanHealth,
    getZcLedger,
    getZcLoanLiquidationThreshold,
    BsMetaUtil,
    calculateCurrentLtv,
    getZcLoanLtv,
    getHealthStatus,
    getLoanCollateralUsdValue,
    CollateralLoanHealthChange
} from "@bridgesplit/abf-react";
import { bsMath } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { MenuItemProps } from "@bridgesplit/ui";

import {
    GroupedLoanActions,
    ManageLoanActions,
    ManualManageSubActions,
    LoanAction,
    LoanManagementActionCategory
} from "../side-card/types";

export function getRolloverPtMint(metadata: TokenListMetadata | undefined): string | undefined {
    return metadata?.metadata?.rolloverPtMint;
}

export function getUnderlyingMint(metadata: TokenListMetadata | undefined): string | undefined {
    return metadata?.metadata?.underlyingMint;
}

export function hasRolloverPtMint(metadata: TokenListMetadata | undefined): boolean {
    return !!getRolloverPtMint(metadata);
}

export async function handleLoanAction(action: LoanAction, onSelect: (action: LoanAction) => void) {
    if (action.onClick) {
        await action.onClick();
        return;
    }
    onSelect(action);
}
export function calculateNewLoanHealth(
    loanExpanded: AbfLoanExpanded | undefined,
    addCollateralAmount: number | undefined,
    collateralUsdPrice: number | undefined
): CollateralLoanHealthChange | undefined {
    const ledgerAccount = getZcLedger(loanExpanded);
    const strategyLtv = getZcLoanLtv(loanExpanded);
    if (!loanExpanded) return undefined;

    const originalCollateralValueUsd = getLoanCollateralUsdValue(loanExpanded);

    // Return early if no changes to avoid unnecessary calculations
    if (!addCollateralAmount) {
        const { health: originalHealth, ltv, liquidationThreshold } = calculateLoanHealth(loanExpanded);
        const originalHealthStatus = getHealthStatus(ltv, strategyLtv, liquidationThreshold);
        return {
            newHealth: originalHealth,
            originalHealth,
            originalCollateralValueUsd,
            newLoanCollateralValueUsd: originalCollateralValueUsd,
            strategyLtv: strategyLtv ?? 0,
            liquidationThreshold,
            originalHealthStatus,
            newHealthStatus: originalHealthStatus
        };
    }

    const { totalOutstanding } = summarizeLedgerAccount(ledgerAccount);
    const collateralValueDelta = bsMath.mul(addCollateralAmount, collateralUsdPrice ?? 0) ?? 0;
    const newLoanCollateralValueUsd = Math.max(originalCollateralValueUsd + collateralValueDelta, 0);

    const totalOwedValue = bsMath.mul(totalOutstanding, loanExpanded.principalUsdPrice ?? 0);

    const { health: originalHealth } = calculateLoanHealth(loanExpanded);

    const liquidationThreshold = getZcLoanLiquidationThreshold(loanExpanded);

    const newHealth = calculateHealthRatio(totalOwedValue, newLoanCollateralValueUsd, liquidationThreshold);
    const newLtv = calculateCurrentLtv({ totalOwedValue, loanCollateralValue: newLoanCollateralValueUsd });
    const originalLtv = calculateCurrentLtv({ totalOwedValue, loanCollateralValue: originalCollateralValueUsd });

    const result = {
        strategyLtv: strategyLtv ?? 0,
        newHealth,
        originalHealth,
        originalCollateralValueUsd,
        newLoanCollateralValueUsd,
        liquidationThreshold,
        originalHealthStatus: getHealthStatus(originalLtv, strategyLtv, liquidationThreshold),
        newHealthStatus: getHealthStatus(newLtv, strategyLtv, liquidationThreshold)
    };

    return result;
}

/**
 * Creates a warning message for transaction that may fail due to insufficient balance
 *
 * @param isAutoSlippage Whether auto-slippage is enabled
 * @param tokenAMetadata Metadata for token A
 * @param tokenBMetadata Metadata for token B
 * @param maxTokenAAmount Maximum amount of token A that may be used
 * @param maxTokenBAmount Maximum amount of token B that may be used
 * @param userTokenAAmount User's available balance of token A
 * @param userTokenBAmount User's available balance of token B
 * @returns Warning message or undefined if no warning is needed
 */
export function getMaxBalanceBalanceWarning(
    isAutoSlippage: boolean,
    tokenAMetadata: TokenListMetadata,
    tokenBMetadata: TokenListMetadata,
    maxTokenAAmount: number | undefined,
    maxTokenBAmount: number | undefined,
    userTokenAAmount: number,
    userTokenBAmount: number
): string | undefined {
    if (maxTokenAAmount && maxTokenAAmount > userTokenAAmount) {
        return `Transaction may fail due to insufficient ${BsMetaUtil.getSymbol(
            tokenAMetadata
        )} balance. Ensure there is ${BsMetaUtil.formatAmount(
            tokenAMetadata,
            maxTokenAAmount
        )} available in your wallet to prevent failures.`;
    }

    if (maxTokenBAmount && maxTokenBAmount > userTokenBAmount) {
        return `Transaction may fail due to insufficient ${BsMetaUtil.getSymbol(
            tokenBMetadata
        )} balance. Ensure there is ${BsMetaUtil.formatAmount(
            tokenBMetadata,
            maxTokenBAmount
        )} available in your wallet to prevent failures.`;
    }

    if (isAutoSlippage) {
        return `Auto-slippage is enabled. Verify the transaction amounts before signing. Transaction may fail due to insufficient balance.`;
    }

    return undefined;
}

export function getOptions(action: ManageLoanActions): { value: ManualManageSubActions; label: string }[] {
    switch (action) {
        case ManageLoanActions.AdjustDebt:
            return [
                {
                    value: ManualManageSubActions.DecreasePrincipal,
                    label: "Repay"
                },
                {
                    value: ManualManageSubActions.IncreasePrincipal,
                    label: "Borrow More"
                }
            ];
        case ManageLoanActions.AdjustCollateral:
            return [
                {
                    value: ManualManageSubActions.TopUpCollateral,
                    label: "Top-up"
                },
                {
                    value: ManualManageSubActions.WithdrawCollateral,
                    label: "Withdraw"
                }
            ];
        case ManageLoanActions.ManualManage:
            return [
                {
                    value: ManualManageSubActions.ManualIncreaseLeverage,
                    label: "Increase"
                },
                {
                    value: ManualManageSubActions.ManualDecreaseLeverage,
                    label: "Decrease"
                }
            ];
        case ManageLoanActions.AdjustLeverageDialog:
            return [
                {
                    value: ManualManageSubActions.AutoLeverageDialog,
                    label: "Auto"
                },
                {
                    value: ManualManageSubActions.ManualLeverageDialog,
                    label: "Manual"
                }
            ];
        default:
            return [];
    }
}

type LoanActionMenuSections = MenuItemProps[][];

export function useLoanActionMenuItemSections(
    actionGroups: GroupedLoanActions,
    onActionClick: (action: LoanAction) => void
): LoanActionMenuSections {
    const leverageSection: MenuItemProps[] = useMemo(
        () =>
            actionGroups[LoanManagementActionCategory.Leverage]
                .filter((a) => !a.hide)
                .map((a) => ({
                    text: a.buttonDropdownText,
                    icon: a.icon,
                    disabled: a.disabled,
                    onClick: a.disabled ? undefined : () => onActionClick(a)
                })),
        [actionGroups, onActionClick]
    );

    const adjustSection: MenuItemProps[] = useMemo(
        () =>
            actionGroups[LoanManagementActionCategory.DebtAndCollateral].map((a) => ({
                text: a.buttonDropdownText,
                icon: a.icon,
                disabled: a.disabled,
                onClick: a.disabled ? undefined : () => onActionClick(a)
            })),
        [actionGroups, onActionClick]
    );

    const miscSection: MenuItemProps[] = useMemo(
        () =>
            actionGroups[LoanManagementActionCategory.Miscellaneous].map((a) => ({
                text: a.buttonDropdownText,
                icon: a.icon,
                disabled: a.disabled,
                onClick: a.disabled ? undefined : () => onActionClick(a)
            })),
        [actionGroups, onActionClick]
    );

    return [leverageSection, adjustSection, miscSection];
}
